import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { useContextSources, useContextSourceBranches } from '@/hooks'
import { dedupeRepositoryContextSources } from '@/components/projects/workflows/shared/contextSourceDedup'
import type { TriggerInputField } from '@/types/workflowDefinition'
import type { ContextSourceListItem } from '@/types'

interface RepoBranchMapFieldProps {
  projectId: string
  field: TriggerInputField
  value: string
  selectedContextSourceIds: string[]
  onChange: (serialized: string) => void
}

export function RepoBranchMapField({
  projectId,
  field,
  value,
  selectedContextSourceIds,
  onChange,
}: Readonly<RepoBranchMapFieldProps>) {
  const [open, setOpen] = useState(false)
  const { data: contextData, isLoading } = useContextSources(
    projectId,
    { maxResultCount: 1000 },
    undefined,
    { enabled: selectedContextSourceIds.length > 0 },
  )

  const repos: ContextSourceListItem[] = useMemo(() => {
    const ids = new Set(selectedContextSourceIds)
    const selectedRepos = (contextData?.items ?? []).filter((s) => ids.has(s.id) && s.kind === 'Repository')
    return dedupeRepositoryContextSources(selectedRepos)
  }, [contextData?.items, selectedContextSourceIds])

  const branchMap: Record<string, string> = useMemo(() => {
    if (!value) return {}
    try {
      return JSON.parse(value) as Record<string, string>
    } catch {
      return {}
    }
  }, [value])

  useEffect(() => {
    const valid = new Set(repos.map((r) => r.id))
    const pruned = Object.fromEntries(Object.entries(branchMap).filter(([id]) => valid.has(id)))
    if (Object.keys(pruned).length !== Object.keys(branchMap).length) {
      onChange(Object.keys(pruned).length ? JSON.stringify(pruned) : '')
    }
  }, [repos, branchMap, onChange])

  const setBranch = (sourceId: string, branch: string) => {
    const next = { ...branchMap }
    if (branch) next[sourceId] = branch
    else delete next[sourceId]
    onChange(Object.keys(next).length ? JSON.stringify(next) : '')
  }

  if (repos.length === 0) return null

  const label = field.label ?? 'Repository branches'

  return (
    <div className="border rounded-md" data-testid={`trigger-input-${field.name}`}>
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium cursor-pointer"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {label}
        <span className="text-muted-foreground font-normal">
          ({repos.length} {repos.length === 1 ? 'repository' : 'repositories'}, optional)
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          {field.description && <p className="text-xs text-muted-foreground">{field.description}</p>}
          {isLoading ? (
            <div className="text-xs text-muted-foreground flex items-center gap-2" role="status" aria-live="polite">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading repositories...
            </div>
          ) : (
            repos.map((repo) => (
              <RepoBranchRow
                key={repo.id}
                projectId={projectId}
                repo={repo}
                value={branchMap[repo.id] ?? ''}
                onChange={(b) => setBranch(repo.id, b)}
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}

function RepoBranchRow({
  projectId,
  repo,
  value,
  onChange,
}: {
  projectId: string
  repo: ContextSourceListItem
  value: string
  onChange: (branch: string) => void
}) {
  const [input, setInput] = useState(value)
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const id = `branch-${repo.id}`

  useEffect(() => {
    setInput(value)
  }, [value])

  useEffect(() => {
    const t = setTimeout(() => setSearch(input.trim()), 300)
    return () => clearTimeout(t)
  }, [input])

  const { data: branches = [], isLoading } = useContextSourceBranches(projectId, repo.id, search, open)

  const commit = (branch: string) => {
    setInput(branch)
    onChange(branch)
    setOpen(false)
  }

  return (
    <div className="relative">
      <Label htmlFor={id} className="text-xs font-medium mb-1 block">{repo.name}</Label>
      <Input
        id={id}
        data-testid={`branch-input-${repo.id}`}
        className="text-sm"
        placeholder="Default branch"
        value={input}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setInput(e.target.value)
          setOpen(true)
        }}
        onBlur={() => {
          onChange(input.trim())
          setTimeout(() => setOpen(false), 150)
        }}
      />
      {open && (
        <div
          className="absolute z-50 mt-1 w-full max-h-48 overflow-auto rounded-md border bg-popover shadow-md"
          role="listbox"
          data-testid={`branch-options-${repo.id}`}
        >
          {isLoading ? (
            <div className="px-2 py-1.5 text-xs text-muted-foreground flex items-center gap-2" role="status" aria-live="polite">
              <Loader2 className="h-3 w-3 animate-spin" /> Searching branches...
            </div>
          ) : branches.length === 0 ? (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">No matching branches — type to use a custom branch name.</div>
          ) : (
            branches.map((b) => (
              <button
                key={b.name}
                type="button"
                role="option"
                aria-selected={b.name === value}
                className="w-full text-left px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                onMouseDown={(e) => {
                  e.preventDefault()
                  commit(b.name)
                }}
              >
                {b.name}{b.isDefault ? ' (default)' : ''}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
